/* A wordless outline of a script template that builds itself while a script
   generates. Colors come from Astryx neutral tokens (resolved inside a
   <Theme> scope) — deliberately monochrome, so the only emphasis is the piece
   being built. Hidden pieces keep their space, so the dialog never resizes
   as the outline fills in. */

.doc {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 16px 18px 18px;
}

.section {
  margin-top: 16px;
}

.question {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 10px;
}

.questionText {
  flex: 1;
  min-width: 0;
}

/* Pieces */

.bar {
  position: relative;
  overflow: hidden;
  border-radius: var(--radius-sm);
  background: var(--color-border-emphasized);
  opacity: 0;
  transform: translateY(4px);
  transition:
    opacity 350ms ease-out,
    transform 350ms ease-out,
    background-color 500ms ease-out;
}

.title   { height: 14px; }
.line    { height: 8px; margin-top: 8px; }
.heading { height: 10px; }
.note    { height: 6px; margin-top: 6px; }
.dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  flex: none;
}
.question .line { margin-top: 1px; }

.w35 { width: 35%; }
.w40 { width: 40%; }
.w45 { width: 45%; }
.w50 { width: 50%; }
.w55 { width: 55%; }
.w60 { width: 60%; }
.w70 { width: 70%; }
.w75 { width: 75%; }
.w80 { width: 80%; }
.w90 { width: 90%; }

/* States */

.visible {
  opacity: 1;
  transform: none;
}

/* A light sweep across pieces that are still being worked on. */
.visible::after {
  content: "";
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, var(--color-background-card), transparent);
  opacity: 0.6;
  animation: skeletonSweep 1.6s ease-in-out infinite;
}

/* The piece being built right now: the darkest bar in light mode, the
   brightest in dark. */
.active {
  background: color-mix(in srgb, var(--color-text-secondary) 55%, transparent);
}

/* Once the script has actually arrived: pieces go quiet and solid, top to
   bottom. */
.settled {
  background: var(--color-background-gray);
}
.settled::after {
  animation: none;
  opacity: 0;
}

@keyframes skeletonSweep {
  to { transform: translateX(100%); }
}

/* Stagger within a group as it reveals (d*), and down the outline as it
   settles (s*). The settle delays win once a piece is settled. */
.d1 { transition-delay: 120ms; }
.d2 { transition-delay: 240ms; }
.d3 { transition-delay: 360ms; }
.d4 { transition-delay: 480ms; }
.d5 { transition-delay: 600ms; }
.d6 { transition-delay: 720ms; }
.d7 { transition-delay: 840ms; }

.settled.s0 { transition-delay: 0ms; }
.settled.s1 { transition-delay: 100ms; }
.settled.s2 { transition-delay: 200ms; }
.settled.s3 { transition-delay: 300ms; }

@media (prefers-reduced-motion: reduce) {
  .bar {
    transform: none;
    transition: none;
  }

  .visible::after {
    animation: none;
    opacity: 0;
  }
}
